import React, {useCallback, useState, useMemo, useEffect, useContext} from 'react';
import {Set, List, Map} from 'immutable';
import {debounce} from 'lodash';
import {useTracking} from 'react-tracking';
import classnames from 'classnames';

import {
  Dialogue,
  Button,
  SearchInput,
  Modal,
  Text,
  IconButton,
  WithTooltip,
  Icon
} from '@albert-io/atomic';
import {ClassroomModelV1} from '@albert-io/models';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';

import StandardSetFilter from 'client/components/AdvancedSearch/StandardsSubSearch/StandardSetFilter.react';

import {StandardPreferences} from 'client/components/QuestionTypes/shared/TagsStandardsList/TagsStandardsList.react';

import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';

import FacetFilter from '../../SubjectAndGuideSelector/FacetFilter/FacetFilter.react';

import StandardsResults from './StandardsResults.react';
import {
  getStandardSetQuery,
  getSearchResults,
  getPersonalizedStandardSetsQuery,
  getClassroomsWithSubjectsQuery
} from './StandardsModal.queries';
import {StandardSet, StandardSetObjectType} from './StandardsModal.types';

import '../../SubjectAndGuideSelector/Modal.scss';

const getFormattedResults = (results, selectedCollection?) => {
  let formattedResults = Map<string, StandardSetObjectType>({});

  results?.forEach((standard) => {
    if (standard) {
      const standardSet = standard.getStandardSet();
      const standardSetId = standardSet.getId();
      let isSetSelected = false;
      let isStandardSelected = false;
      let isExpanded = false;
      let isIndeterminate = false;

      if (selectedCollection) {
        isSetSelected = selectedCollection.getIn([standardSetId, 'isSelected'], false);
        isExpanded = selectedCollection.getIn([standardSetId, 'isExpanded'], false);
        isIndeterminate = selectedCollection.getIn([standardSetId, 'isIndeterminate'], false);
        isStandardSelected = selectedCollection.getIn(
          [standardSetId, 'standards', standard.getId(), 'isSelected'],
          false
        );
      }
      const standardObj = Map({
        standard,
        isSelected: isStandardSelected
      });
      // Create and set the standard set object with the set that it came from
      if (!formattedResults.has(standardSetId)) {
        const standardSetObj = Map({
          standardSet,
          isExpanded,
          isSelected: isSetSelected,
          isIndeterminate,
          standards: Map([[standard.getId(), standardObj]])
        });
        formattedResults = formattedResults.set(standardSetId, standardSetObj);

        formattedResults = formattedResults.set(standardSetId, standardSetObj);
      }
      // Add the standard to the parent standard set that already exists
      else {
        formattedResults = formattedResults.setIn(
          [standardSetId, 'standards', standard.getId()],
          standardObj
        );
      }
    }
  });
  return formattedResults;
};
interface StandardsModalProps {
  handleClose: () => void;
  personalizationSettings: List<StandardSet>;
  classrooms: List<ClassroomModelV1>;
}

const StandardsModal = ({
  handleClose,
  personalizationSettings,
  classrooms
}: StandardsModalProps) => {
  const {
    standardsSearchString: globalString,
    setStandardsSearchString: setGlobalString,
    standardCollection,
    setStandardCollection,
    standardModalResults,
    setStandardModalResults,
    filteredStandardSets,
    setFilteredStandardSets
  } = useContext(ContentDiscoveryContext);

  // filtering/loading states
  const [searchString, setSearchString] = useState(globalString);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStandardSets, setSelectedStandardSets] =
    useState<Set<StandardSet>>(filteredStandardSets);

  // displayed search results persistent across closing and reopening of standards modal if standards are aplied
  const [formattedResults, setFormattedResults] =
    useState<Map<string, StandardSetObjectType>>(standardModalResults);

  // displayed search results that get flushed upon closing of standards modal when standards are not applied
  const [unsavedStandardCollection, setUnsavedStandardCollection] =
    useState<Map<string, StandardSetObjectType>>(standardCollection);

  // whether to highlight standards/standard sets matchigng search keyword
  const [showMatching, setShowMatching] = useState(false);

  // whether personalization toggle is switched on
  const [filterPersonalization, setFilterPersonalization] = useState(
    !personalizationSettings.isEmpty()
  );

  useEffect(() => {
    setFilteredStandardSets(selectedStandardSets);
  }, [selectedStandardSets, setFilteredStandardSets]);

  const fetchResults = useCallback(async () => {
    setIsLoading(true);
    const standardSets = selectedStandardSets.map((standardSet) => standardSet.getId()).join(',');
    const fetchedData = await getSearchResults(
      standardSets,
      searchString,
      filterPersonalization,
      classrooms
    );
    setUnsavedStandardCollection((prevResults) => {
      const newFormattedResults = getFormattedResults(fetchedData);
      const updatedStandardsCollection = newFormattedResults.merge(prevResults);
      const updatedFormattedResults = getFormattedResults(fetchedData, updatedStandardsCollection);
      setFormattedResults(updatedFormattedResults);
      return updatedStandardsCollection;
    });

    if (searchString.length > 0) {
      setShowMatching(true);
    } else {
      setShowMatching(false);
    }
    setIsLoading(false);
  }, [
    selectedStandardSets,
    searchString,
    setUnsavedStandardCollection,
    setFormattedResults,
    filterPersonalization,
    classrooms
  ]);

  const debouncedFetch = useMemo(() => debounce(fetchResults, 500), [fetchResults]);

  useEffect(() => {
    debouncedFetch();
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  // Update functions
  const updateMapValue = (obj, keys, newValue) => {
    if (obj?.hasIn(keys)) {
      // Use setIn to update the value at the specified key path
      return obj.setIn(keys, newValue);
    }
    return obj;
  };

  const hasSelection = unsavedStandardCollection.some((set) => {
    return !!(set?.get('isSelected') || set?.get('isIndeterminate'));
  });

  return (
    <Modal
      ariaLabel='Standards Modal'
      handleClose={handleClose}
      dismissable={false}
      data-testid='assignment-builder__standards-modal'
    >
      {({CloseButtonWrapper}) => (
        <Dialogue className='cd-modal__container' inModal paddingLevel={0} size='xl' hideCloseBtn>
          <div className='cd-modal__header'>
            <div className='cd-modal__title u-display_flex u-display_flex u-align-items_center u-pad-l_3 u-pad-r_1 u-pad-tb_1 u-border-b u-border-color_slate-300'>
              <Text bold size='m' className='u-flex-grow_1'>
                Find Standards
              </Text>
              <CloseButtonWrapper>
                <IconButton size='s' icon='times' label='Close' onClick={handleClose} />
              </CloseButtonWrapper>
            </div>
            <div className='u-display_flex u-display_flex u-align-items_center u-justify-content_space-between u-pad-l_3 u-pad-r_1 u-pad-tb_1 u-border-b u-border-color_slate-300'>
              <div className='u-display_flex u-gap_1 u-mar-l_auto'>
                <StandardSetFilter
                  onApply={(standardSets) => setSelectedStandardSets(Set(standardSets))}
                  standardSets={List(selectedStandardSets)}
                />

                <PersonalizationToggle
                  on={filterPersonalization}
                  onToggle={() => {
                    setFilterPersonalization(!filterPersonalization);
                  }}
                />
                <SearchInput
                  variant
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                  onReset={() => setSearchString('')}
                  className={classnames('u-mar-r_1 cd-modal__search-input', {
                    'cd-modal__search-input--active': searchString.length > 0
                  })}
                  placeholder='Search by standard code or description'
                />
              </div>
            </div>
          </div>
          <Dialogue.Body className='cd-modal__body'>
            <StandardsResults
              updateMapValue={updateMapValue}
              formattedResults={formattedResults}
              setFormattedResults={setFormattedResults}
              setUnsavedStandardCollection={setUnsavedStandardCollection}
              unsavedStandardCollection={unsavedStandardCollection}
              isLoading={isLoading}
              showMatches={showMatching}
              filteredStandardSets={filteredStandardSets}
            />
          </Dialogue.Body>
          <div className='cd-modal__footer' role='group'>
            <Button
              disabled={!hasSelection}
              onClick={() => {
                let newStandardList = List();
                let newStandardSetList = List();

                unsavedStandardCollection?.valueSeq().forEach((setObj) => {
                  if (setObj?.get('isSelected')) {
                    newStandardSetList = newStandardSetList.push(setObj.get('standardSet'));
                  } else {
                    setObj
                      ?.get('standards')
                      // @ts-ignore
                      .valueSeq()
                      .forEach((standardObj) => {
                        if (standardObj.get('isSelected')) {
                          newStandardList = newStandardList.push(standardObj.get('standard'));
                        }
                      });
                  }
                });
                setStandardCollection(unsavedStandardCollection);
                setStandardModalResults(formattedResults);
                setGlobalString(searchString);
                handleClose();
              }}
            >
              Apply
            </Button>
          </div>
        </Dialogue>
      )}
    </Modal>
  );
};

export default awaitMandarkQueries(
  () => ({
    queries: {
      personalizationSettings: getPersonalizedStandardSetsQuery(),
      classrooms: getClassroomsWithSubjectsQuery()
    }
  }),
  StandardsModal
);

type PersonalizationToggleProps = {on: boolean; onToggle: () => void};
const PersonalizationToggle = ({on, onToggle}: PersonalizationToggleProps) => {
  return (
    <div className='u-display_flex u-align-items_center'>
      <Button
        data-testid='standards-modal__personalization-toggle-btn'
        color={on ? 'primary' : 'secondary'}
        variant={on ? 'faded' : 'outlined'}
        onClick={onToggle}
      >
        <Icon icon={on ? 'eye' : 'eye-slash'} className='u-mar-r_1' />
        Preferences: {on ? 'On' : 'Off'}{' '}
        <WithTooltip theme='light' content={<StandardPreferences />}>
          <Icon
            icon={['far', 'info-circle']}
            className='u-mar-l_1 u-cursor_pointer'
            color='inherit'
            data-testid='standards-modal__personalization-tooltip'
          />
        </WithTooltip>
      </Button>
    </div>
  );
};

// BEGIN original standards modal, to be deleted upon QA approval of content personalization feature
export const StandardsModalFallback = ({handleClose}: {handleClose: () => void}) => {
  const {
    standardsSearchString: globalString,
    setStandardsSearchString: setGlobalString,
    standardCollection,
    setStandardCollection,
    standardModalResults,
    setStandardModalResults,
    filteredStandardSets,
    setFilteredStandardSets
  } = useContext(ContentDiscoveryContext);

  const {Track, trackEvent} = useTracking({
    component: 'Standards modal'
  });

  const trackedClose = (content: string) => {
    handleClose();
    trackEvent({type: 'button', event: 'click', content});
  };

  const trackSelection = (item: StandardSet) => {
    trackEvent({type: 'select', event: 'click', content: `${item.getTitle()}`});
  };

  const trackSearch = (search: string, componentName: string) => {
    trackEvent({
      type: 'search bar',
      event: 'search',
      component: componentName,
      content: search
    });
  };

  const [searchString, setSearchString] = useState(globalString);
  const [selectedStandardSets, setSelectedStandardSets] =
    useState<Set<StandardSet>>(filteredStandardSets);
  const [formattedResults, setFormattedResults] =
    useState<Map<string, StandardSetObjectType>>(standardModalResults);
  const [unsavedStandardCollection, setUnsavedStandardCollection] =
    useState<Map<string, StandardSetObjectType>>(standardCollection);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmpty, setIsEmptyState] = useState(standardModalResults.isEmpty());
  const [showMatching, setShowMatching] = useState(false);

  useEffect(() => {
    setFilteredStandardSets(selectedStandardSets);
  }, [selectedStandardSets]);

  const hideResults = useMemo(
    () => !searchString.length && selectedStandardSets.size < 1,
    [searchString, selectedStandardSets]
  );

  const fetchResults = useCallback(async () => {
    setIsLoading(true);

    if (hideResults) {
      return;
    }

    const standardSets = selectedStandardSets.map((standardSet) => standardSet.getId()).join(',');
    const fetchedData = await getSearchResults(standardSets, searchString);
    setUnsavedStandardCollection((prevResults) => {
      const newFormattedResults = getFormattedResults(fetchedData);
      const updatedStandardsCollection = newFormattedResults.merge(prevResults);
      const updatedFormattedResults = getFormattedResults(fetchedData, updatedStandardsCollection);
      setFormattedResults(updatedFormattedResults);
      return updatedStandardsCollection;
    });

    if (searchString.length > 0) {
      setShowMatching(true);
    } else {
      setShowMatching(false);
    }
    setIsLoading(false);
  }, [
    selectedStandardSets,
    searchString,
    hideResults,
    setUnsavedStandardCollection,
    setFormattedResults
  ]);

  const debouncedFetch = useMemo(() => debounce(fetchResults, 500), [fetchResults]);

  useEffect(() => {
    debouncedFetch();
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  useEffect(() => {
    if ((searchString.length > 0 || !selectedStandardSets.isEmpty()) && isEmpty) {
      setIsEmptyState(false);
    } else if (searchString.length === 0 && selectedStandardSets.isEmpty() && !isEmpty) {
      setIsEmptyState(true);
    }
  }, [selectedStandardSets, searchString, isEmpty, setIsEmptyState]);

  // Update functions
  const updateMapValue = (obj, keys, newValue) => {
    if (obj?.hasIn(keys)) {
      // Use setIn to update the value at the specified key path
      return obj.setIn(keys, newValue);
    }
    return obj;
  };

  const hasSelection = unsavedStandardCollection.some((set) => {
    return !!(set?.get('isSelected') || set?.get('isIndeterminate'));
  });

  return (
    <Track>
      <Modal ariaLabel='Standards Modal' handleClose={handleClose} dismissable={false}>
        {({CloseButtonWrapper}) => (
          <Dialogue className='cd-modal__container' inModal paddingLevel={0} size='xl' hideCloseBtn>
            <div className='cd-modal__header'>
              <div className='cd-modal__title u-display_flex u-display_flex u-align-items_center u-pad-l_3 u-pad-r_1 u-pad-tb_1 u-border-b u-border-color_slate-300'>
                <Text bold size='m' className='u-flex-grow_1'>
                  Find Standards
                </Text>
                <CloseButtonWrapper>
                  <IconButton
                    size='s'
                    icon='times'
                    label='Close'
                    onClick={() => trackedClose('x out')}
                  />
                </CloseButtonWrapper>
              </div>
              <div className='u-display_flex u-display_flex u-align-items_center u-justify-items_space-between u-pad-l_3 u-pad-r_1 u-pad-tb_1 u-border-b u-border-color_slate-300'>
                <FacetFilter<StandardSet>
                  buttonText='Standard Set'
                  onSelect={setSelectedStandardSets}
                  onSelectTrack={trackSelection}
                  onSearchTrack={(s) => trackSearch(s, 'standards set search')}
                  content={(item) => item.getTitle()}
                  searchQuery={getStandardSetQuery}
                  selectedItems={selectedStandardSets}
                  onClear={() => {
                    setSelectedStandardSets(Set());
                    trackEvent({content: 'removed_all', type: 'filter button'});
                  }}
                  helpText='Search for standard sets such as “Common Core Math” or “ACT WorkKey Skills”'
                />
                <SearchInput
                  variant
                  data-testid='standards-modal__search-input'
                  value={searchString}
                  onChange={(e) => {
                    setSearchString(e.target.value);
                  }}
                  onBlur={() =>
                    searchString.length > 0 && trackSearch(searchString, 'standard search')
                  }
                  onReset={() => setSearchString('')}
                  className={classnames('u-mar-l_auto cd-modal__search-input', {
                    'cd-modal__search-input--active': searchString.length > 0
                  })}
                  placeholder='Search by standard code or description'
                />
              </div>
            </div>
            <Dialogue.Body className='cd-modal__body'>
              {!hideResults && !isEmpty && (
                <StandardsResults
                  updateMapValue={updateMapValue}
                  formattedResults={formattedResults}
                  setFormattedResults={setFormattedResults}
                  setUnsavedStandardCollection={setUnsavedStandardCollection}
                  unsavedStandardCollection={unsavedStandardCollection}
                  isLoading={isLoading}
                  showMatches={showMatching}
                  filteredStandardSets={filteredStandardSets}
                />
              )}
              {isEmpty && <InitialResults />}
            </Dialogue.Body>
            <div className='cd-modal__footer' role='group'>
              <Button
                disabled={!hasSelection}
                onClick={() => {
                  let newStandardList = List();
                  let newStandardSetList = List();

                  unsavedStandardCollection?.valueSeq().forEach((setObj) => {
                    if (setObj?.get('isSelected')) {
                      newStandardSetList = newStandardSetList.push(setObj.get('standardSet'));
                    } else {
                      setObj
                        ?.get('standards')
                        // @ts-ignore
                        .valueSeq()
                        .forEach((standardObj) => {
                          if (standardObj.get('isSelected')) {
                            newStandardList = newStandardList.push(standardObj.get('standard'));
                          }
                        });
                    }
                  });
                  setStandardCollection(unsavedStandardCollection);
                  setStandardModalResults(formattedResults);
                  setGlobalString(searchString);
                  handleClose();
                }}
              >
                Apply
              </Button>
            </div>
          </Dialogue>
        )}
      </Modal>
    </Track>
  );
};
// END original standards modal

const InitialResults = () => {
  return (
    <div className='cd-modal__empty-container'>
      <div className='cd-standards-modal__empty-message-container u-pad-tb_6 u-pad-lr_2 u-display_flex u-justify-content_center u-flex-direction_column u-text-align_center u-border-radius_1 u-bgc_white'>
        <Text>
          Click &quot;Choose Standard Set&quot; to find a state-specific or national standard set.
        </Text>
        <Text>Or use the search bar to find a specific standard.</Text>
      </div>
    </div>
  );
};
